import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { listStepConnections } from "./whatsappFlowApi";
import { flowRoleLabel } from "./whatsappFlowCatalogs";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ButtonAdd = styled(Button)({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: "#161e55",
  color: "#ffff",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
});

const cellText = (value) => (value != null && String(value).trim() !== "" ? value : "—");

export default function FlujosWhatsappList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listStepConnections();
      setRows(data);
    } catch (e) {
      setError(e?.message || "No se pudo cargar la lista de flujos.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box sx={{ width: "100%", margin: "18px 14px" }}>
        <Stack spacing={2}>
          <Item>.</Item>
          <Item></Item>
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <h1
                    style={{
                      margin: 0,
                      color: "#161e55",
                    }}
                  >
                    Flujos WhatsApp
                  </h1>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Lista de flujos configurados. Puedes crear uno nuevo.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <ButtonAdd
                    variant="contained"
                    onClick={() => navigate("/homeAdmin/flujos-whatsapp/segmentaciones")}
                  >
                    Segmentaciones
                  </ButtonAdd>
                  <ButtonAdd
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/homeAdmin/flujos-whatsapp/nuevo")}
                  >
                    Crear flujo
                  </ButtonAdd>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress sx={{ color: "#161e55" }} />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} size="small" aria-label="Tabla de flujos WhatsApp">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Nombre
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 600 }}>
                      Descripción
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Tipo
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No hay flujos registrados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.uuid}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell align="center">{cellText(row.name)}</TableCell>
                        <TableCell
                          align="left"
                          sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                        >
                          {cellText(row.description)}
                        </TableCell>
                        <TableCell align="center">
                          {flowRoleLabel(row.flowRole || "PRIMARY")}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/homeAdmin/flujos-whatsapp/${row.uuid}`)}
                            sx={{
                              textTransform: "none",
                              color: "#161e55",
                              borderColor: "#161e55",
                            }}
                            variant="outlined"
                          >
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Box>
      <Toolbar />
    </React.Fragment>
  );
}
